import type { CSSProperties, MouseEvent } from 'react';
import { forwardRef, useMemo } from 'react';
import type { LinkValue } from '@elfsight/configurator-contracts';
import { openWindowByLink, validateLink } from '@elfsight/widget-utils';
import { Layout } from '../../../types';
import { useBanner } from '../../bannerProvider';
import type { MediaBlockProps } from '../components';
import { MediaBlock } from '../components';
import type { BaseImageBlockProps } from './BaseImageBlock';
import { BaseImageBlock } from './BaseImageBlock';
import { imageSettings } from './imageSettings';
import { SideImageBlock } from './SideImageBlock';

export type ImageBlockProps = BaseImageBlockProps & {
  alignment?: MediaBlockProps['alignment'];
  link?: LinkValue;
  layout?: Layout;
  areaWidth?: number;
  imageHeight?: number;
  applyMobileSettings: boolean;
  mobileLayout?: Layout;
  mobileScale: number;
  mobileRatio: number;
  horizontalSize?: number;
  horizontalSizeOnMobile?: number;
  horizontalBorderRadius?: number;
  verticalBorderRadius?: number;
  isFormSubmitted: boolean;
};

export const ImageBlock = forwardRef<HTMLDivElement, ImageBlockProps>(
  (
    {
      alignment,
      link,
      scale = imageSettings.scale,
      ratio = imageSettings.ratio,
      style,
      imageHeight,
      applyMobileSettings,
      layout = imageSettings.layout,
      mobileLayout = imageSettings.mobileLayout,
      mobileScale = imageSettings.mobileScale,
      mobileRatio = imageSettings.mobileRatio,
      horizontalSize,
      horizontalSizeOnMobile,
      horizontalBorderRadius,
      verticalBorderRadius,
      isFormSubmitted,
      onClick,
      ...forwardingProps
    },
    forwardingRef
  ) => {
    const { contentDirection, mobileView } = useBanner();

    const isColumn = contentDirection === 'column';

    const defaultScale = applyMobileSettings
      ? imageSettings.mobileScale
      : imageSettings.scale;

    const defaultRatio = applyMobileSettings
      ? imageSettings.mobileRatio
      : imageSettings.ratio;

    const currentLayout = applyMobileSettings ? mobileLayout : layout;

    const isSideMediaLayout = [Layout.LeftMedia, Layout.RightMedia].includes(
      currentLayout
    );
    const isSideBannerLayout = [Layout.LeftBanner, Layout.RightBanner].includes(
      currentLayout
    );
    const isEmbeddedMedia =
      currentLayout === Layout.EmbeddedMedia ||
      (mobileView && isSideMediaLayout);
    const isEmbeddedBanner =
      currentLayout === Layout.EmbeddedBanner ||
      (mobileView && isSideBannerLayout);

    const borderRadius = useMemo(() => {
      if (!isColumn) {
        return horizontalBorderRadius;
      }

      if (isEmbeddedBanner || isSideBannerLayout) {
        return;
      }

      return verticalBorderRadius;
    }, [
      isColumn,
      isEmbeddedBanner,
      isSideBannerLayout,
      horizontalBorderRadius,
      verticalBorderRadius
    ]);

    const calculatedScale = useMemo(() => {
      const value = applyMobileSettings ? mobileScale : scale;
      const isMediaLayout = isEmbeddedMedia || isSideMediaLayout;

      if (isColumn && isMediaLayout) {
        return value;
      }

      return defaultScale;
    }, [
      applyMobileSettings,
      mobileScale,
      scale,
      isEmbeddedMedia,
      isSideMediaLayout,
      isColumn,
      defaultScale
    ]);

    const calculatedRatio = useMemo(() => {
      const value = applyMobileSettings ? mobileRatio : ratio;

      if (isColumn && isEmbeddedBanner) {
        return value;
      }

      return defaultRatio;
    }, [
      applyMobileSettings,
      mobileRatio,
      ratio,
      isColumn,
      isEmbeddedBanner,
      defaultRatio
    ]);

    const containerHeight = useMemo(() => {
      if (isColumn) {
        return;
      }

      const size = applyMobileSettings
        ? horizontalSizeOnMobile
        : horizontalSize;

      return `${size}px`;
    }, [isColumn, applyMobileSettings, horizontalSizeOnMobile, horizontalSize]);

    const hasValidLink = !!link && validateLink(link.type, link.rawValue);

    const handleOnClick = hasValidLink
      ? (e: MouseEvent) => {
          e.stopPropagation();

          onClick?.(e);

          if (link) {
            openWindowByLink(e, link);
          }
        }
      : undefined;

    const commonProps = {
      ...(forwardingProps as ImageBlockProps),
      ref: forwardingRef,
      borderRadius,
      containerHeight,
      scale: calculatedScale,
      ratio: calculatedRatio,
      onClick: handleOnClick
    } as ImageBlockProps;

    const commonStyle: CSSProperties = {
      alignItems: 'center',
      cursor: hasValidLink ? 'pointer' : undefined,
      pointerEvents: hasValidLink ? undefined : 'none',
      ...style
    };

    const horizontalBlockStyle = { width: 'auto' };

    const showSideBlock =
      isColumn &&
      !isFormSubmitted &&
      (isSideMediaLayout || isSideBannerLayout) &&
      !mobileView;

    return (
      <>
        {showSideBlock ? (
          <SideImageBlock
            {...commonProps}
            style={commonStyle}
            layout={layout}
          />
        ) : (
          <MediaBlock
            alignment={alignment}
            layout={currentLayout}
            height={imageHeight}
            minWidth={isColumn ? '100px' : undefined}
            style={{
              ...commonStyle,
              ...(!isColumn ? horizontalBlockStyle : {})
            }}
          >
            <BaseImageBlock {...commonProps} />
          </MediaBlock>
        )}
      </>
    );
  }
);
